import type { ComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { DemoSlot } from '../demo-slot/interfaces'

import { useListsStore } from '@shared/model'

import { useDemoShiftControlStore } from './store'
import { useAssignBookingApi } from '../../api/demo-slots/assign-booking.hook'

interface SelectKidOptions {
  tutorSlot: ComputedRef<DemoSlot>
}

interface SelectKidReturn {
  options: ComputedRef<AppSelectOption<number>[]>
  modelValue: ComputedRef<number | undefined>
  disabled: ComputedRef<boolean>
  assignKid: (value: number | undefined) => Promise<void>
}

export const useSelectKid = ({ tutorSlot }: SelectKidOptions): SelectKidReturn => {
  const demoShiftControlStore = useDemoShiftControlStore()
  const listsStore = useListsStore()

  const getCode = (disciplineCode: string | undefined): string => {
    if (!disciplineCode) return '-'
    return listsStore.getLists?.discipline.code?.[disciplineCode] || '-'
  }

  const options = computed<AppSelectOption<number>[]>(() => {
    const items = [...demoShiftControlStore.getWaitingBookings]
    const booking = tutorSlot.value.booking
    if (booking && !items.some(option => option.value === booking.id)) {
      const kidName = booking.discipline?.kid?.name ?? 'Unknown'
      const code = getCode(booking.discipline?.code)
      items.unshift({ value: booking.id, text: `${kidName} (${code})` })
    }
    return items
  })

  const modelValue = computed<number | undefined>(() => tutorSlot.value.booking?.id ?? undefined)

  const disabled = computed<boolean>(() => !tutorSlot.value.booking)

  const assignKid = async (value: number | undefined): Promise<void> => {
    if (value === undefined) return
    await useAssignBookingApi({ slotId: tutorSlot.value.id, bookingId: value })
  }

  return {
    options,
    modelValue,
    disabled,
    assignKid,
  }
}
